import React, { useContext, useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Divider, Text, DropdownItem, SparkIcon, TagColored, TagGeneral } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { usePlanTier } from '@/features/billing/hooks/usePlanTier';
import { FeedbackModal } from '@/features/feedback';
import { MCP_TAB, settingsTabPath } from '@/features/settings/components/settingsTabs.js';
import CreateWorkspaceModal from '@/features/workspaces/components/CreateWorkspaceModal';
import WorkspaceSwitcher from '@/features/workspaces/components/WorkspaceSwitcher';
import '@/app/layout/HorizontalNavBar.css';

/** ``/settings`` plus the usecase id when one is active (same shape the old gear used). */
function settingsPath(usecaseId) {
    return usecaseId ? `/settings?usecaseId=${usecaseId}` : '/settings';
}

export function UserMenu() {
    const navigate = useNavigate();
    const location = useLocation();
    const globalState = useContext(GlobalContext);
    const currentUsecaseId = globalState?.currentUsecaseId;
    const { user, logout, isAuthEnabled, feedbackEnabled, mcpEnabled } = useAuth();
    const planTier = usePlanTier();
    const [open, setOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        function handleOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    const displayName = user?.name || user?.email || '';
    const avatarType = user?.picture ? 'image' : 'initials';

    function handleGiveFeedback() {
        setOpen(false);
        setFeedbackOpen(true);
    }

    function handleAddWorkspace() {
        setOpen(false);
        setCreateWorkspaceOpen(true);
    }

    // Settings pages keep the return location so their Back button lands
    // where the user was.
    function openSettings(path) {
        setOpen(false);
        const returnTo = `${location.pathname}${location.search}${location.hash}`;
        navigate(path, { state: { from: returnTo } });
    }

    function handleSettings() {
        // Figma 9150:4071: Settings moved from the nav gear into this menu.
        openSettings(settingsPath(currentUsecaseId));
    }

    function handleTryMcp() {
        // Figma 9603:17774: the promo row deep-links to Settings → MCP.
        openSettings(settingsTabPath(MCP_TAB.id, currentUsecaseId));
    }

    async function handleLogOut() {
        setOpen(false);
        await logout();
        navigate('/login');
    }

    return (
        <div ref={wrapRef} className="home-nav-user-menu">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="home-nav-user-menu-trigger"
                aria-label="User menu"
                aria-expanded={open}
            >
                <Avatar
                    type={avatarType}
                    src={user?.picture || undefined}
                    primaryText={displayName}
                    size="small"
                />
            </button>

            {open && (
                <div className="home-nav-user-menu-panel">
                    <div className="spark-dropdown home-nav-user-menu-dropdown" role="menu">
                        <div className="home-nav-user-menu-identity">
                            <div className="home-nav-user-menu-identity-row">
                                <Text variant="lead-medium" as="p" className="home-nav-user-menu-name">
                                    {displayName}
                                </Text>
                                <div className="home-nav-user-menu-tags">
                                    {planTier === 'pro' ? (
                                        <TagGeneral label="Pro" endIconSlug="bolt" />
                                    ) : null}
                                    {planTier === 'free' ? <TagColored variant="disabled" label="Free" /> : null}
                                </div>
                            </div>
                            {user?.email ? (
                                <Text variant="detail-regular" color="secondary" as="p">
                                    {user.email}
                                </Text>
                            ) : null}
                        </div>
                        <Divider />
                        <WorkspaceSwitcher onAddWorkspace={handleAddWorkspace} />
                        <Divider />
                        {feedbackEnabled ? (
                            <DropdownItem
                                label="Give us Feedback"
                                showPrefixIcon
                                prefixIconSlug="megaphone"
                                onClick={handleGiveFeedback}
                            />
                        ) : null}
                        <DropdownItem
                            label="Settings"
                            showPrefixIcon
                            prefixIconSlug="settings"
                            onClick={handleSettings}
                        />
                        {isAuthEnabled ? (
                            <DropdownItem
                                label="Log Out"
                                showPrefixIcon
                                prefixIconSlug="arrow-right"
                                onClick={handleLogOut}
                            />
                        ) : null}
                        {mcpEnabled ? (
                            <DropdownItem
                                label="Try Specific’s Remote MCP"
                                tone="promo"
                                prefix={<SparkIcon slug="mcp" size="medium" className="spark-dropdown-item-prefix" />}
                                suffix={<SparkIcon slug="out" size="medium" className="spark-dropdown-item-suffix" />}
                                className="home-nav-user-menu-mcp"
                                onClick={handleTryMcp}
                            />
                        ) : null}
                    </div>
                </div>
            )}
            {feedbackEnabled ? (
                <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
            ) : null}
            <CreateWorkspaceModal open={createWorkspaceOpen} onClose={() => setCreateWorkspaceOpen(false)} />
        </div>
    );
}
